import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Linking, Alert } from 'react-native';
import { Image } from 'expo-image';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Path } from 'react-native-svg';
import PageHeader from '../components/PageHeader';
import { fetchUserClinic, Clinic } from '../services/clinicsApi';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

export default function ProviderScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const { session, sessionReady } = useAuth();
  const [clinicData, setClinicData] = useState<Clinic | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoHeight, setLogoHeight] = useState<number | null>(null);
  const LOGO_WIDTH = 165;

  useEffect(() => {
    if (sessionReady && session?.access_token) {
      loadClinicData();
    }
    // C3 (PV2-16): drop access_token from deps — rotates on auto-refresh.
  }, [sessionReady]);

  const loadClinicData = async () => {
    try {
      console.log('🏥 Loading clinic data for provider page...');
      const data = await fetchUserClinic(session?.access_token);
      if (data) {
        console.log('🏥 Clinic data loaded:', {
          name: data.name,
          logo_path: data.logo_path,
          description: data.description,
          id: data.id
        });
        setClinicData(data);
      } else {
        console.log('🏥 No clinic found for user');
      }
    } catch (error) {
      console.error("🏥 Failed to fetch clinic data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.back();
  };

  const handlePhonePress = () => {
    if (clinicData?.phone) {
      const phoneNumber = clinicData.phone.replace(/\D/g, '');
      Linking.openURL(`tel:${phoneNumber}`);
    }
  };

  const handleWhatsAppPress = () => {
    if (clinicData?.whatsapp) {
      const whatsappNumber = clinicData.whatsapp.replace(/\D/g, '');
      Linking.openURL(`https://wa.me/55${whatsappNumber}`);
    }
  };

  const handleEmailPress = () => {
    if (clinicData?.email) {
      Linking.openURL(`mailto:${clinicData.email}`);
    }
  };

  const handleWebsitePress = (website: string) => {
    const url = website.startsWith('http') ? website : `https://${website}`;
    router.push({
      pathname: '/webview',
      params: { url: url, title: t('account.provider.websiteTitle') }
    });
  };

  const formatWebsite = (website: string) => {
    return website.replace(/^https?:\/\//, '').replace(/\/$/, '');
  };

  const handleInstagramPress = (instagram: string) => {
    if (instagram) {
      let instagramUrl = instagram;
      if (instagramUrl.includes('instagram.com/')) {
        // It's already a full URL
        Linking.openURL(instagramUrl);
      } else {
        // It's just a username
        const username = instagramUrl.replace('@', '');
        Linking.openURL(`https://instagram.com/${username}`);
      }
    }
  };

  const formatInstagramUsername = () => {
    if (!clinicData?.instagram) return null;

    let username = clinicData.instagram;

    // Extract username from URL
    if (username.includes('instagram.com/')) {
      const urlParts = username.split('instagram.com/');
      if (urlParts.length > 1) {
        username = urlParts[1].replace('/', '');
      }
    }

    // Remove @ if already present and add it back
    username = username.replace('@', '');
    return `@${username}`;
  };

  const handleAddressPress = (address: string) => {
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      Linking.openURL(`https://maps.google.com/?q=${encodedAddress}`);
    }
  };

  const renderLogo = () => {
    if (clinicData?.logo_path) {
      return (
        <Image
          source={{ uri: clinicData.logo_path }}
          style={[
            styles.clinicLogo,
            logoHeight !== null ? { height: logoHeight } : { height: 60 },
          ]}
          contentFit="contain"
          onLoad={(e) => {
            const { width, height } = e.source;
            if (width && height) {
              const ratio = height / width;
              setLogoHeight(Math.round(LOGO_WIDTH * ratio));
            }
          }}
          onError={() => {
            setLogoHeight(60);
          }}
        />
      );
    }

    return (
      <Text style={styles.providerNameText}>
        {clinicData?.name || t('account.provider.title')}
      </Text>
    );
  };

  const formatSchedule = (schedule: any) => {
    if (!schedule) return null;

    if (typeof schedule === 'string') {
      try {
        const scheduleData = JSON.parse(schedule);
        return formatScheduleArray(scheduleData);
      } catch {
        return schedule;
      }
    }

    if (Array.isArray(schedule)) {
      return formatScheduleArray(schedule);
    }

    return t('account.provider.schedule');
  };

  const formatScheduleArray = (scheduleArray: any[]) => {
    if (!scheduleArray || scheduleArray.length === 0) return null;

    const activeDays = scheduleArray.filter(item => item.checked);
    if (activeDays.length === 0) return null;

    // Group days by same time schedule
    const timeGroups: { [key: string]: string[] } = {};

    activeDays.forEach(day => {
      const timeKey = `${day.startTime}-${day.endTime}`;
      const dayName = day.day.replace(' das', '');

      if (!timeGroups[timeKey]) {
        timeGroups[timeKey] = [];
      }
      timeGroups[timeKey].push(dayName);
    });

    const formattedGroups = Object.entries(timeGroups).map(([timeKey, days]) => {
      const [startTime, endTime] = timeKey.split('-');
      const formattedStart = startTime.replace(':', 'h');
      const formattedEnd = endTime.replace(':', 'h');

      let dayText = '';

      // Check for common patterns
      const weekdays = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira'];
      const weekends = ['Sábado', 'Domingo'];

      const hasAllWeekdays = weekdays.every(day => days.includes(day));
      const hasAllWeekends = weekends.every(day => days.includes(day));

      if (hasAllWeekdays && hasAllWeekends) {
        dayText = 'Segunda a domingo';
      } else if (hasAllWeekdays) {
        dayText = 'Segunda a sexta';
      } else if (hasAllWeekends) {
        dayText = 'Finais de semana';
      } else if (days.length === 1) {
        dayText = days[0];
      } else {
        // Custom grouping
        dayText = days.join(', ');
      }

      return `${dayText} das ${formattedStart} às ${formattedEnd}`;
    });

    return formattedGroups.join('. ');
  };

  const formatAddress = () => {
    if (!clinicData) return null;

    const addressParts = [
      clinicData.street && clinicData.number ? `${clinicData.street}, ${clinicData.number}` : clinicData.street,
      clinicData.complement,
      clinicData.neighborhood,
      clinicData.city,
      clinicData.state,
      clinicData.zipcode
    ].filter(Boolean);

    return addressParts.length > 0 ? addressParts.join(' - ') : null;
  };

  const PhoneIcon = () => (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <Path d="M18.4487 6.16549L18.4498 17.8336C18.4498 19.9999 16.6937 21.756 14.5274 21.756H8.54937C6.38408 21.756 4.62802 19.9999 4.62802 17.8347L4.62695 6.16656C4.62695 4.00021 6.38302 2.24414 8.54832 2.24414H14.5274C16.6926 2.24414 18.4487 4.00021 18.4487 6.16549Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M10.0312 6.24219H13.0488" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M11.5388 17.7783V17.726M11.5388 17.4517C11.389 17.4517 11.2676 17.5731 11.2676 17.7224C11.2676 17.8722 11.389 17.9937 11.5388 17.9937C11.6887 17.9937 11.8101 17.8722 11.8101 17.7224C11.8101 17.5731 11.6887 17.4517 11.5388 17.4517Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </Svg>
  );

  const ScheduleIcon = () => (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <Path d="M15.1523 3.69238V6.6677" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M7.92969 3.69238V6.6677" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path fillRule="evenodd" clipRule="evenodd" d="M15.683 5.06445L7.40863 5.06527C4.97812 5.06527 3.46094 6.41874 3.46094 8.90651V16.3957C3.46094 18.9229 4.97812 20.3077 7.40863 20.3077L15.6759 20.3068C18.1136 20.3068 19.6235 18.9462 19.6235 16.4576V8.90569C19.6307 6.41792 18.1207 5.06445 15.683 5.06445Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M7.52539 11.4375H7.53539" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M7.52539 14.8916H7.53539" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M11.5371 11.4375H11.5471" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M11.5371 14.8916H11.5471" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M15.541 11.4375H15.551" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M15.541 14.8916H15.551" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </Svg>
  );

  const EmailIcon = () => (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <Path d="M17.9033 8.85107L13.46 12.4641C12.6205 13.1301 11.4394 13.1301 10.5999 12.4641L6.11914 8.85107" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path fillRule="evenodd" clipRule="evenodd" d="M16.9089 21C19.9502 21.0084 22 18.5095 22 15.4384V8.57001C22 5.49883 19.9502 3 16.9089 3H7.09114C4.04979 3 2 5.49883 2 8.57001V15.4384C2 18.5095 4.04979 21.0084 7.09114 21H16.9089Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </Svg>
  );

  const WebsiteIcon = () => (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <Path fillRule="evenodd" clipRule="evenodd" d="M12 3C16.9709 3 21 7.02908 21 12C21 16.9709 16.9709 21 12 21C7.02908 21 3 16.9709 3 12C3 7.02908 7.02908 3 12 3Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path fillRule="evenodd" clipRule="evenodd" d="M11.9984 3C13.849 3 15.3483 7.02908 15.3483 12C15.3483 16.9709 13.849 21 11.9984 21C10.1478 21 8.64844 16.9709 8.64844 12C8.64844 7.02908 10.1478 3 11.9984 3Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M3 11.998H21" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </Svg>
  );

  const InstagramIcon = () => (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Path d="M7.78216 3H16.2169C19.165 3 21 5.08119 21 8.02638V15.9736C21 18.9188 19.165 21 16.2159 21H7.78216C4.83405 21 3 18.9188 3 15.9736V8.02638C3 5.08119 4.84281 3 7.78216 3Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M11.8445 8.82812C9.91023 8.82812 8.3418 10.3966 8.3418 12.3308C8.3418 14.2651 9.91023 15.8335 11.8445 15.8335C13.7788 15.8335 15.3472 14.2651 15.3472 12.3308C15.3472 10.3966 13.7788 8.82812 11.8445 8.82812Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path d="M16.8651 7.36374V7.43274M17.1471 7.37875C17.1471 7.53575 17.0201 7.66275 16.8631 7.66275C16.7061 7.66275 16.5781 7.53575 16.5781 7.37875C16.5781 7.22175 16.7061 7.09375 16.8631 7.09375C17.0201 7.09375 17.1471 7.22175 17.1471 7.37875Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </Svg>
  );

  const AddressIcon = () => (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Path d="M4.97266 10.5269C4.97266 15.8822 11.294 21 12.4986 21C13.7031 21 20.0245 15.8822 20.0245 10.5269C20.0245 6.36941 16.6551 3 12.4986 3C8.34207 3 4.97266 6.36941 4.97266 10.5269Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <Path fillRule="evenodd" clipRule="evenodd" d="M15.0079 10.4654C15.0079 9.07984 13.885 7.95703 12.4995 7.95703C11.113 7.95703 9.99023 9.07984 9.99023 10.4654C9.99023 11.8509 11.113 12.9747 12.4995 12.9747C13.885 12.9747 15.0079 11.8509 15.0079 10.4654Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </Svg>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <PageHeader title={t('account.provider.title')} onBack={handleBack} backgroundColor="#FFFFFF" />
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t('account.provider.loading')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  const formattedAddress = formatAddress();
  const scheduleText = formatSchedule(clinicData?.schedule);

  return (
    <SafeAreaView style={styles.container}>
      <PageHeader title={t('account.provider.title')} onBack={handleBack} backgroundColor="#FFFFFF" />

      <ScrollView style={styles.scrollView}>
        {/* Provider Info Section */}
        <View style={styles.section}>
          <View style={styles.logoContainer}>
            {renderLogo()}
          </View>

          {clinicData?.description && (
            <Text style={styles.description}>
              {clinicData.description}
            </Text>
          )}

          {/* Schedule Section */}
          {scheduleText && (
            <View style={styles.scheduleCard}>
              <ScheduleIcon />
              <View style={styles.scheduleContent}>
                <Text style={styles.scheduleTitle}>{t('account.provider.attendance')}</Text>
                <Text style={styles.scheduleText}>{scheduleText}</Text>
              </View>
            </View>
          )}
        </View>

        {/* Contact Section */}
        {(clinicData?.phone || clinicData?.whatsapp || clinicData?.email || clinicData?.website || clinicData?.instagram) && (
          <>
            <View style={styles.divider} />
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('account.provider.contactChannels')}</Text>

              {/* Phone */}
              {clinicData?.phone && (
                <TouchableOpacity style={styles.menuItem} onPress={handlePhonePress}>
                  <View style={styles.menuItemIcon}>
                    <PhoneIcon />
                  </View>
                  <View style={styles.menuItemContent}>
                    <Text style={styles.menuItemTitle}>{t('account.provider.phone')}</Text>
                    <Text style={styles.menuItemSubtitle}>{clinicData.phone}</Text>
                  </View>
                  <View style={styles.chevronContainer}>
                    <Ionicons name="chevron-forward" size={16} color="#151515" />
                  </View>
                </TouchableOpacity>
              )}

              {/* WhatsApp */}
              {clinicData?.whatsapp && (
                <TouchableOpacity style={styles.menuItem} onPress={handleWhatsAppPress}>
                  <View style={styles.menuItemIcon}>
                    <Ionicons name="logo-whatsapp" size={24} color="#151515" />
                  </View>
                  <View style={styles.menuItemContent}>
                    <Text style={styles.menuItemTitle}>{t('account.provider.whatsapp')}</Text>
                    <Text style={styles.menuItemSubtitle}>{clinicData.whatsapp}</Text>
                  </View>
                  <View style={styles.chevronContainer}>
                    <Ionicons name="chevron-forward" size={16} color="#151515" />
                  </View>
                </TouchableOpacity>
              )}

              {/* Email */}
              {clinicData?.email && (
                <TouchableOpacity style={styles.menuItem} onPress={handleEmailPress}>
                  <View style={styles.menuItemIcon}>
                    <EmailIcon />
                  </View>
                  <View style={styles.menuItemContent}>
                    <Text style={styles.menuItemTitle}>{t('account.provider.email')}</Text>
                    <Text style={styles.menuItemSubtitle}>{clinicData.email}</Text>
                  </View>
                  <View style={styles.chevronContainer}>
                    <Ionicons name="chevron-forward" size={16} color="#151515" />
                  </View>
                </TouchableOpacity>
              )}

              {/* Website */}
              {clinicData?.website && (
                <TouchableOpacity style={styles.menuItem} onPress={() => handleWebsitePress(clinicData.website)}>
                  <View style={styles.menuItemIcon}>
                    <WebsiteIcon />
                  </View>
                  <View style={styles.menuItemContent}>
                    <Text style={styles.menuItemTitle}>{t('account.provider.website')}</Text>
                    <Text style={styles.menuItemSubtitle}>{formatWebsite(clinicData.website)}</Text>
                  </View>
                  <View style={styles.chevronContainer}>
                    <Ionicons name="chevron-forward" size={16} color="#151515" />
                  </View>
                </TouchableOpacity>
              )}

              {/* Instagram */}
              {clinicData?.instagram && (
                <TouchableOpacity style={styles.menuItem} onPress={() => handleInstagramPress(clinicData.instagram)}>
                  <View style={styles.menuItemIcon}>
                    <InstagramIcon />
                  </View>
                  <View style={styles.menuItemContent}>
                    <Text style={styles.menuItemTitle}>{t('account.provider.instagram')}</Text>
                    <Text style={styles.menuItemSubtitle}>{formatInstagramUsername()}</Text>
                  </View>
                  <View style={styles.chevronContainer}>
                    <Ionicons name="chevron-forward" size={16} color="#151515" />
                  </View>
                </TouchableOpacity>
              )}
            </View>
          </>
        )}

        {/* Address Section */}
        {formattedAddress && (
          <>
            <View style={styles.divider} />
            <View style={styles.section}>
              <TouchableOpacity style={styles.menuItem} onPress={() => handleAddressPress(formattedAddress)}>
                <View style={styles.menuItemIcon}>
                  <AddressIcon />
                </View>
                <View style={styles.menuItemContent}>
                  <Text style={styles.menuItemTitle}>{t('account.provider.address')}</Text>
                  <Text style={styles.menuItemSubtitle}>{formattedAddress}</Text>
                </View>
                <View style={styles.chevronContainer}>
                  <Ionicons name="chevron-forward" size={16} color="#151515" />
                </View>
              </TouchableOpacity>
            </View>
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#757C85',
  },
  scrollView: {
    flex: 1,
  },
  section: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  logoContainer: {
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  clinicLogo: {
    width: 165,
    resizeMode: 'contain',
    alignSelf: 'flex-start',
  },
  providerNameText: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    marginBottom: 12,
  },
  description: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 20,
    marginBottom: 24,
  },
  scheduleCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
  },
  scheduleContent: {
    flex: 1,
  },
  scheduleTitle: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    marginBottom: 4,
  },
  scheduleText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 20,
  },
  divider: {
    height: 1,
    backgroundColor: '#E7EAED',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    marginBottom: 16,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 16,
  },
  menuItemIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuItemContent: {
    flex: 1,
  },
  menuItemTitle: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    lineHeight: 19.6,
  },
  menuItemSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#757C85',
    lineHeight: 19.6,
  },
  chevronContainer: {
    marginLeft: 'auto',
  },

});